
import type { FilterParams, RestaurantWithEventSpaces } from "@/types/filters";
import { EVENT_TYPES } from "@/lib/constants";

/**
 * Main filtering utility for restaurant listings.
 * This module consolidates filter operations for the primary view.
 */

export const filterRestaurants = (
  restaurants: RestaurantWithEventSpaces[],
  filters: FilterParams
): RestaurantWithEventSpaces[] => {
  if (!restaurants || !filters) return [];

  let filtered = [...restaurants];

  // Apply neighborhood filter
  if (filters.neighborhood) {
    const normalizedFilter = filters.neighborhood.trim().toLowerCase();
    filtered = filtered.filter(r => r.neighborhood?.name?.trim().toLowerCase() === normalizedFilter);
  }

  // Apply search filter
  if (filters.search) {
    const search = filters.search.toLowerCase();
    filtered = filtered.filter(r => 
      r.name.toLowerCase().includes(search) ||
      r.description?.toLowerCase().includes(search)
    );
  }

  // Apply event type filter
  if (filters.eventType) {
    // We only want to filter by valid event types
    if (EVENT_TYPES.includes(filters.eventType as any)) {
      filtered = filtered.filter(r => 
        r.eventspaces.some(space => {
          if (!space.event_types) return false;
          
          const spaceEventTypes = space.event_types
            .split(',')
            .map(type => type.trim());
          
          return spaceEventTypes.includes(filters.eventType);
        })
      );
    }
  }

  // Apply group size filter
  if (filters.groupSize) {
    const capacityRanges = {
      small: { min: 0, max: 20 },
      medium: { min: 20, max: 50 },
      large: { min: 50, max: Infinity }
    };

    const range = capacityRanges[filters.groupSize as keyof typeof capacityRanges];
    if (range) {
      filtered = filtered.filter(r =>
        r.eventspaces.some(space => {
          const capacity = parseInt(space.capacity || '0');
          return capacity >= range.min && capacity <= range.max;
        })
      );
    }
  }

  // Apply price range filter
  if (filters.priceRange) {
    const priceRanges = {
      '1': { max: 1000 },
      '2': { max: 5000 },
      '3': { max: 10000 },
      '4': { max: Infinity }
    };

    const range = priceRanges[filters.priceRange as keyof typeof priceRanges];
    if (range) {
      filtered = filtered.filter(r =>
        r.eventspaces.some(space => {
          const maxSpend = Math.max(...(space.pricing_tiers || []).map(tier => tier.max_spend || 0));
          return maxSpend <= range.max;
        })
      );
    }
  }

  return filtered;
}
